import { useEffect, useMemo } from 'react';
import { ACCEPTABLE_URL_PARAMS, FilterTypes, INTIAL_FILTERS } from '../constants/initialFilters';
import {
  ComplexFilterOptions,
  QUERY_FILTERS_OPTIONS,
  SimpleAndRegularFilterOptions,
} from '../constants/queryFiltersConfig';
import { filterInitialState } from '../utils/analyticsEventListeners';

type CarlineTypes = {
  [key: string]: string[];
};

const useQueryFilters = (): FilterTypes | null => {
  //   const filterLSData = useFilterData();
  const searchParams = new URLSearchParams(window.location.search);

  const hasFilterParams = ACCEPTABLE_URL_PARAMS.some((param) => searchParams.has(param));

  // Function that will transform filter that have label Complex (carline), because each query
  // value will effect on carline + body type
  const complexFiltersProcessor = (singleFilterData: ComplexFilterOptions, type: string) => {
    const filterQueryValues = searchParams
      .get(type)
      ?.split(',')
      .map((param) => param.toLocaleLowerCase());
    const complexFilterOptions = Object.keys(singleFilterData.options);
    const acceptableFilters = filterQueryValues
      ?.filter((toCheck) => complexFilterOptions.includes(toCheck))
      .map((singleFilter) => singleFilterData.options[singleFilter])
      .reduce((acc, curr) => {
        Object.entries(curr).forEach(([key, value]) => {
          acc[key] = [...(acc[key] || []), ...value];
        });
        return acc;
      }, {});
    if (type === 'carline-group' && acceptableFilters) return acceptableFilters.modelFamily;
    return acceptableFilters;
  };

  // Function that will transform filter that have label Regular - filters that can have multiply
  // values in one array
  const regularFilterProcessor = (
    singleFilterData: SimpleAndRegularFilterOptions,
    type: string,
  ) => {
    const filterQueryValues = searchParams
      .get(type)
      ?.split(',')
      .map((param) => {
        if (param === 'allroad-quattro' || param === 'allroadquattro') return 'allroad_quattro';
        if (param === 'bonus') return 'credit';
        if (param === 'toronto') return 'GTA';
        if (param === 'montreal') return 'MTL';
        if (param === 'vancouver') return 'VAN';
        return param.toLocaleLowerCase();
      });
    const acceptableFilters = [
      ...new Set(
        filterQueryValues?.filter((toCheck) => singleFilterData.options.includes(toCheck)),
      ),
    ];
    return acceptableFilters;
  };

  // Function that will transform filter that have label Simple - filters that can have only 1 value and it is string
  const simpleFilterProcessor = (type: string) => {
    const filterQueryValues = searchParams.get(type)?.split(',')[0];
    if (filterQueryValues === 'credit') return 'bonus';
    if (filterQueryValues === 'new') return 'NEW-CAR';
    if (filterQueryValues === 'cpo') return 'USED-CAR';
    return filterQueryValues;
  };

  // Mapping different filters based on the filter type
  const urlParamGetter = (type: string) => {
    if (!searchParams.get(type)) {
      return [];
    }

    const singleFilterData = QUERY_FILTERS_OPTIONS[type];

    if (singleFilterData?.filterType === 'complex') {
      const complexFilters = complexFiltersProcessor(
        singleFilterData as ComplexFilterOptions,
        type,
      );
      return complexFilters;
    }
    if (singleFilterData?.filterType === 'regular') {
      const regularFilters = regularFilterProcessor(
        singleFilterData as SimpleAndRegularFilterOptions,
        type,
      );
      return regularFilters;
    }
    if (singleFilterData?.filterType === 'simple') {
      const simpleFilter = simpleFilterProcessor(type);
      return simpleFilter;
    }
    return [];
  };

  // We are grabbing each individual filter from the query params
  // A little bit more lines, but easy to debug in the future.

  const carlineGroup = urlParamGetter('carline-group') as string[];
  const carline = urlParamGetter('carline') as CarlineTypes;
  const bodyStyle = urlParamGetter('body-style') as string[];
  const trimline = urlParamGetter('trimline') as string[];
  const year = urlParamGetter('year') as string[];
  const offerType = urlParamGetter('offer-type') as string[];
  const offerRegion = urlParamGetter('region') as string[];
  const vehicleType = urlParamGetter('type') as string;

  const hasAnyAcceptedFilters = !![
    ...carlineGroup,
    ...bodyStyle,
    ...Object.keys(carline),
    ...year,
    ...trimline,
    ...year,
    ...offerType,
    ...offerRegion,
    ...vehicleType,
  ].length;

  // Preparing all data for the LS

  const combinedFiltersFromQuery = useMemo(() => {
    return {
      offerType: [...offerType],
      modelFamily: [...new Set([...(carline.modelFamily ?? []), ...carlineGroup])],
      bodyType: [...new Set([...(carline.bodyType ?? []), ...bodyStyle])],
      modelYear: [...year],
      trimLine: [...trimline],
      offerRegion: [...offerRegion],
      vehicleType: vehicleType.length ? vehicleType : 'NEW-CAR',
    };
  }, [
    offerType,
    carline.modelFamily,
    carline.bodyType,
    carlineGroup,
    bodyStyle,
    year,
    trimline,
    offerRegion,
    vehicleType,
  ]);

  const listToUpload = useMemo(() => {
    // If NO prev LS data and no Other filters (except Type) - we are passing
    // Initial filters + Type filter (if no type - default "new")
    if (!hasAnyAcceptedFilters) {
      return INTIAL_FILTERS;
    }

    // In all other cases we are passing what we got from the URL params
    return combinedFiltersFromQuery;
  }, [hasAnyAcceptedFilters, combinedFiltersFromQuery]);

  // IXIS event to send inital filter from query params only durign first page load
  useEffect(() => {
    filterInitialState(listToUpload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasFilterParams) return null;

  return listToUpload;
};

export default useQueryFilters;
